import { ClubService } from "../../../../services/clubService";
import { UploadService } from "../../../../services/uploadService";
import {
  applySettledUploadResults,
  getRetryablePhotoAssets,
  getSessionUploadsForCleanup,
  mergeGalleryUrls,
} from "../../../../utils/adminPhotoHandoff";
import { normalizeLink } from "../../../../utils/clubLinks";
import { isLocalUri } from "../../lib/files";
import { clearPending, markPending } from "../../lib/pendingWork";
import { signedInAs } from "../../lib/session";
import { msg } from "./manageUtils";

/**
 * Edit Club Bio save, mirroring AdminScreen.handleSaveBio (:1690-1923) step for step.
 * Resolves { ok: true } or { error: string }. `ctx`:
 *   form, clubId, uid, photos (useBioPhotos), loadUserClubs, onPatch(fields), notify(message, icon)
 */
export async function saveBio({ form, clubId, uid, photos, loadUserClubs, onPatch, notify }) {
  if (!form.name.trim()) return { error: "Club name cannot be empty" };
  if (!form.description.trim()) return { error: "Description cannot be empty" };
  if (!uid) return { error: "You must be signed in to do this." };

  // Logo: a brand-new local file is attached in the background after the primary write.
  const logoNeedsUpload = !!form.logo && isLocalUri(form.logo);
  let immediateLogoUrl;
  if (form.logo === null) immediateLogoUrl = null;
  else if (!logoNeedsUpload && form.logo?.startsWith("http")) immediateLogoUrl = form.logo;

  const keptPdfs = form.pdfs.filter((pdf) => pdf.url || !pdf.uri || pdf.uri.startsWith("http"));
  const pendingPdfs = form.pdfs.filter((pdf) => pdf.uri && !pdf.url && !pdf.uri.startsWith("http"));

  const keptGallery = (Array.isArray(form.gallery) ? form.gallery : []).filter(
    (u) => typeof u === "string" && u.length > 0 && u.startsWith("http")
  );
  const uploadCandidates = photos.stagedRef.current.filter(
    (a) => a.status === "selected" || a.status === "failed"
  );
  let nextStaged = photos.stagedRef.current;
  if (uploadCandidates.length > 0) {
    photos.setUploading(true);
    try {
      const { data, error } = await UploadService.uploadClubGalleryImagesSettled(clubId, uploadCandidates);
      if (error) return { error: msg(error) };
      nextStaged = applySettledUploadResults(photos.stagedRef.current, [
        ...(data?.uploaded || []),
        ...(data?.failed || []),
      ]);
      photos.updateStaged(nextStaged);
      if (getRetryablePhotoAssets(nextStaged).length > 0) {
        return { error: "Some photos could not upload. Retry the failed photos before saving. Your saved gallery has not been changed." };
      }
    } finally {
      photos.setUploading(false);
    }
  }
  const finalGallery = mergeGalleryUrls(keptGallery, nextStaged);

  const persistedLinks = form.links
    .map(normalizeLink)
    .filter((link) => link.url.trim())
    .map((link) => ({ url: link.url.trim(), name: link.name.trim(), is_members_only: link.is_members_only }));

  const updateData = {
    name: form.name.trim(),
    description: form.description.trim(),
    links: persistedLinks,
    pdfs: keptPdfs,
    gallery: finalGallery,
  };
  if (form.category_id) {
    updateData.category = form.category;
    updateData.category_id = form.category_id;
  }
  if (!logoNeedsUpload) updateData.logo_url = immediateLogoUrl;

  if (!signedInAs(uid)) return { error: "You were signed out or switched accounts. Nothing was saved." };
  const { error } = await ClubService.updateClub(clubId, updateData, uid);
  if (error) {
    // A timeout may still have committed; re-read before any cleanup so saved URLs are never deleted.
    const reconciliation = await UploadService.reconcileAmbiguousClubGallerySave({
      clubId,
      uid,
      uploads: [...photos.retryRef.current, ...getSessionUploadsForCleanup(nextStaged)],
      readSavedGallery: async (id) => ClubService.getClubById(id),
    });
    photos.setCleanupRetry(reconciliation.data?.failed || []);
    return { error: "Failed to update club bio. Please try again." };
  }

  const pendingLogoUri = logoNeedsUpload ? form.logo : null;
  onPatch(updateData);
  loadUserClubs?.().catch?.(() => {});
  const cleanup = await UploadService.cleanupUnsavedClubGalleryUploads({
    clubId,
    uid,
    uploads: [...photos.retryRef.current, ...getSessionUploadsForCleanup(nextStaged)],
    savedGalleryUrls: finalGallery,
  });
  photos.setCleanupRetry(cleanup.data?.failed || []);
  photos.reset();

  // Background: attach a new logo / new PDFs, then patch the club doc once (second write).
  if (pendingLogoUri || pendingPdfs.length > 0) {
    markPending(`club:${clubId}`); // Save stays disabled until the second write lands (EditBioTab)
    attachInBackground({ clubId, uid, pendingLogoUri, pendingPdfs, keptPdfs, loadUserClubs, onPatch, notify });
  }
  return { ok: true, saved: { ...updateData, gallery: finalGallery } };
}

async function attachInBackground({ clubId, uid, pendingLogoUri, pendingPdfs, keptPdfs, loadUserClubs, onPatch, notify }) {
  const sameAccount = () => signedInAs(uid); // the account that started the save, not whoever is current now
  try {
    if (!sameAccount()) return; // the browser's account changed: never write with another account's token
    const ts = Date.now();
    const [logoUrl, newPdfs] = await Promise.all([
      pendingLogoUri
        ? UploadService.uploadImageViaREST(pendingLogoUri, `club-logos/${clubId}/${uid}`, `club-logo-${Date.now()}.jpg`).then(
            (r) => {
              if (r?.error || !r?.data?.url) throw r?.error || new Error("Logo upload failed");
              return r.data.url;
            }
          )
        : Promise.resolve(undefined),
      Promise.all(
        pendingPdfs.map((pdf, i) =>
          UploadService.uploadPDFViaREST(pdf.uri, "club-documents", `club-document-${ts}-${i}.pdf`).then((r) => {
            if (r?.error || !r?.data?.url) throw r?.error || new Error("Document upload failed");
            return { name: pdf.name || "document", url: r.data.url };
          })
        )
      ),
    ]);
    if (!sameAccount()) return;
    const finalUpdate = { pdfs: [...keptPdfs, ...newPdfs] };
    if (pendingLogoUri) finalUpdate.logo_url = logoUrl;
    const { error } = await ClubService.updateClub(clubId, finalUpdate, uid);
    if (error) throw error;
    onPatch(finalUpdate);
    loadUserClubs?.().catch?.(() => {});
    notify("Logo and documents attached");
  } catch (e) {
    notify(`Saved, but the logo or documents didn't finish uploading: ${msg(e)}`, "alert");
  } finally {
    clearPending(`club:${clubId}`);
  }
  // The blob: URIs are deliberately not revoked here: the form may still hold them if the admin kept editing.
}
